const isExternalUrl = (url: string): boolean => {
  if (!url) return false;
  if (url.startsWith("data:") || url.startsWith("blob:") || url.startsWith("/")) return false;
  try {
    return new URL(url, window.location.href).origin !== window.location.origin;
  } catch {
    return false;
  }
};

export const loadImage = (src: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    // CRITICAL: set crossOrigin BEFORE src for external images
    if (isExternalUrl(src)) {
      img.crossOrigin = "anonymous";
    }

    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
};

const drawCover = (
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  width: number,
  height: number
) => {
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  if (!iw || !ih) return;

  const scale = Math.max(width / iw, height / ih);
  const sw = width / scale;
  const sh = height / scale;
  const sx = (iw - sw) / 2;
  const sy = (ih - sh) / 2;

  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, width, height);
};

export const canvasToBlob = (canvas: HTMLCanvasElement): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) return reject(new Error("Failed to export canvas to blob"));
        resolve(blob);
      },
      "image/png",
      1
    );
  });
};

/**
 * Composes a foreground PNG (with transparency) on top of a background image (cover).
 * Guarantees the background is present even when DOM rasterization drops it on iOS.
 */
export const composePngOnBackground = async (params: {
  width: number;
  height: number;
  backgroundSrc: string;
  foregroundBlob: Blob;
}): Promise<Blob> => {
  const { width, height, backgroundSrc, foregroundBlob } = params;

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Failed to get canvas context");

  // Always fill with white first — prevents transparent output if bg image fails
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, width, height);

  // Background
  try {
    const bgImg = await loadImage(backgroundSrc);
    drawCover(ctx, bgImg, width, height);
  } catch (e) {
    console.warn("[composePng] background image load failed, using white fallback:", e);
  }

  // Foreground (blob URL is same-origin)
  const fgUrl = URL.createObjectURL(foregroundBlob);
  try {
    const fgImg = await loadImage(fgUrl);
    ctx.drawImage(fgImg, 0, 0, width, height);
  } finally {
    URL.revokeObjectURL(fgUrl);
  }

  return await canvasToBlob(canvas);
};
